<template>
  <div class="chart-container">
    <!-- Tab Navigation -->
    <div class="tabs-wrapper">
      <div class="tabs">
        <button
          v-for="(tab, index) in tabs"
          :key="index"
          :class="['tab-btn', { active: activeTab === tab.key }]"
          @click="activeTab = tab.key"
        >
          <span class="tab-icon">{{ tab.icon }}</span>
          {{ tab.label }}
        </button>
      </div>
    </div>

    <!-- Charts -->
    <div class="chart-content" v-if="chartOptions">
      <!-- Pie Chart -->
      <apexchart
        v-if="activeTab === 'pie'"
        type="pie"
        height="600"
        :options="pieOptions"
        :series="series"
      />

      <!-- Donut Chart -->
      <apexchart
        v-if="activeTab === 'donut'"
        type="donut"
        height="600"
        :options="donutOptions"
        :series="series"
      />

      <!-- Bar Chart (Horizontal) -->
      <apexchart
        v-if="activeTab === 'bar'"
        type="bar"
        :height="barChartHeight"
        :options="barOptions"
        :series="barSeries"
      />

      <!-- Treemap Chart -->
      <apexchart
        v-if="activeTab === 'treemap'"
        type="treemap"
        height="600"
        :options="treemapOptions"
        :series="treemapSeries"
      />

      <!-- Radial Bar Chart -->
      <apexchart
        v-if="activeTab === 'radial'"
        type="radialBar"
        height="600"
        :options="radialOptions"
        :series="radialSeries"
      />
    </div>

    <!-- Loading -->
    <div v-else class="loading">
      <span>Yükleniyor...</span>
    </div>
  </div>
</template>

<script>
/*
firstFundReport
*/
import http from "../services/http";
import VueApexCharts from 'vue-apexcharts';

export default {
  components:{
    apexchart: VueApexCharts,
  },
  data() {
    return {
      activeTab: 'bar',
      tabs: [
        { key: 'bar', label: 'Bar Chart', icon: '📊' },
        { key: 'pie', label: 'Pie Chart', icon: '🥧' },
        { key: 'donut', label: 'Donut Chart', icon: '🍩' },
        { key: 'treemap', label: 'Treemap', icon: '🗂️' },
        { key: 'radial', label: 'Radial', icon: '🎯' },
      ],
      series: [],
      labels: [],
      rawData: [],
      chartOptions: null,
    };
  },
  computed: {
    barChartHeight() {
      // Her agent için 40px yükseklik
      return Math.max(400, this.rawData.length * 40);
    },
    pieOptions() {
      return {
        labels: this.labels,
        legend: {
          position: 'right',
          fontSize: '14px',
          width: 300,
          itemMargin: {
            horizontal: 5,
            vertical: 3
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            return val.toFixed(1) + '%';
          },
          style: {
            fontSize: '12px',
          }
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return val + ' lead';
            }
          }
        },
        responsive: [{
          breakpoint: 480,
          options: {
            legend: {
              position: 'bottom'
            }
          }
        }]
      };
    },
    donutOptions() {
      return {
        labels: this.labels,
        legend: {
          position: 'right',
          fontSize: '14px',
          width: 300,
          itemMargin: {
            horizontal: 5,
            vertical: 3
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '55%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Toplam',
                  fontSize: '16px',
                  fontWeight: 600,
                }
              }
            }
          }
        },
        dataLabels: {
          enabled: false
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return val + ' lead';
            }
          }
        }
      };
    },
    barOptions() {
      return {
        chart: {
          type: 'bar',
          toolbar: {
            show: true
          }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: '70%',
            distributed: true,
            dataLabels: {
              position: 'top'
            }
          }
        },
        colors: this.generateColors(this.rawData.length),
        dataLabels: {
          enabled: true,
          textAnchor: 'start',
          formatter: function(val) {
            return val;
          },
          offsetX: 5,
          style: {
            fontSize: '14px',
            fontWeight: 'bold',
            colors: ['#333']
          }
        },
        xaxis: {
          categories: this.labels,
          labels: {
            style: {
              fontSize: '14px'
            }
          }
        },
        yaxis: {
          labels: {
            style: {
              fontSize: '13px'
            },
            maxWidth: 200
          }
        },
        legend: {
          show: false
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return val + ' lead';
            }
          }
        }
      };
    },
    barSeries() {
      return [{
        name: 'Lead Count',
        data: this.series
      }];
    },
    treemapOptions() {
      return {
        chart: {
          type: 'treemap',
          toolbar: {
            show: true
          }
        },
        legend: {
          show: false
        },
        dataLabels: {
          enabled: true,
          style: {
            fontSize: '14px',
          },
          formatter: function(text, op) {
            return [text, op.value + ' lead'];
          },
          offsetY: -4
        },
        plotOptions: {
          treemap: {
            distributed: true,
            enableShades: false
          }
        },
        colors: this.generateColors(this.rawData.length)
      };
    },
    treemapSeries() {
      return [{
        data: this.rawData.map(item => ({
          x: item.agent_name,
          y: item.lead_count
        }))
      }];
    },
    radialOptions() {
      const maxValue = Math.max(...this.series);
      return {
        chart: {
          type: 'radialBar',
        },
        plotOptions: {
          radialBar: {
            offsetY: 0,
            startAngle: 0,
            endAngle: 270,
            hollow: {
              margin: 5,
              size: '30%',
              background: 'transparent',
            },
            dataLabels: {
              name: {
                show: true,
                fontSize: '14px',
              },
              value: {
                show: true,
                fontSize: '14px',
                formatter: function(val) {
                  return Math.round(val * maxValue / 100);
                }
              },
              total: {
                show: true,
                label: 'Toplam',
                formatter: () => {
                  return this.series.reduce((a, b) => a + b, 0);
                }
              }
            }
          }
        },
        colors: this.generateColors(this.rawData.length),
        labels: this.labels.slice(0, 10), // Radial chart için max 10 göster
        legend: {
          show: true,
          position: 'left',
          fontSize: '12px',
        }
      };
    },
    radialSeries() {
      const maxValue = Math.max(...this.series);
      // Radial chart için yüzdelik değerler hesapla ve max 10 tane göster
      return this.series.slice(0, 10).map(val => Math.round((val / maxValue) * 100));
    }
  },
  mounted() {
    this.fetchData();
    setInterval(() => {
      this.fetchData();
    }, 600000);
  },
  methods: {
    generateColors(count) {
      const colors = [
        '#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0',
        '#546E7A', '#26a69a', '#D10CE8', '#FF9800', '#4CAF50',
        '#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5',
        '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#8BC34A',
        '#CDDC39', '#FFEB3B', '#FFC107', '#FF5722', '#795548',
        '#607D8B', '#9E9E9E', '#000000', '#1A237E', '#B71C1C'
      ];
      // Renk sayısı yetmezse tekrar et
      const result = [];
      for (let i = 0; i < count; i++) {
        result.push(colors[i % colors.length]);
      }
      return result;
    },
    fetchData() {
      http.firstFundReport()
        .then(response => {
          const data = response.data;

          // Verileri lead_count'a göre büyükten küçüğe sırala
          const sortedData = [...data].sort((a, b) => b.lead_count - a.lead_count);

          this.rawData = sortedData;
          this.labels = sortedData.map(item => item.agent_name + ' (' + item.lead_count + ')');
          this.series = sortedData.map(item => item.lead_count);

          this.chartOptions = { loaded: true };
        })
        .catch(error => {
          console.error('Error fetching data:', error);
        });
    }
  }
};
</script>

<style scoped>
.chart-container {
  padding: 20px;
  background: #fff;
  min-height: 100vh;
}

.tabs-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.tabs {
  display: flex;
  gap: 8px;
  background: #f5f5f5;
  padding: 8px;
  border-radius: 12px;
  flex-wrap: wrap;
  justify-content: center;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 20px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  color: #666;
  transition: all 0.2s ease;
}

.tab-btn:hover {
  background: #e0e0e0;
  color: #333;
}

.tab-btn.active {
  background: #1976d2;
  color: white;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3);
}

.tab-icon {
  font-size: 18px;
}

.chart-content {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  font-size: 18px;
  color: #666;
}
</style>
